// api.js - For integrating with Django backend

export const API_URL = process.env.NODE_ENV === 'production'
  ? 'api'
  : `http://localhost:8000/api`;

/**
 * Check if an email already exists in the system
 * @param {string} email - The email to check
 * @returns {Promise<boolean>} - Whether the email exists
 */
export const checkEmailExists = async (email) => {
  try {
    const response = await fetch(`${API_URL}/tenants/check-email/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    
    const data = await response.json();
    return data.exists;
  } catch (error) {
    console.error('Error checking email:', error);
    return false;
  }
};

/**
 * Submit user profile data (first step)
 * @param {Object} userData - The user profile data
 * @returns {Promise<Object>} - Response from the API
 */
export const submitUserProfile = async (userData) => {
  try {
    const response = await fetch(`${API_URL}/tenants/profile-setup/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        first_name: userData.firstName,
        last_name: userData.lastName,
        email: userData.email,
        phone: userData.phone,
        role: userData.role,
        wa_plan: userData.waPlan
      }),
    });
    
    return await response.json();
  } catch (error) {
    console.error('Error submitting user profile:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Submit company details and complete the onboarding process
 * @param {Object} companyData - The company data
 * @param {number} leadId - The ID of the lead created in the first step
 * @returns {Promise<Object>} - Response from the API
 */
export const submitCompanyDetails = async (companyData, leadId) => {
  try {
    const response = await fetch(`${API_URL}/tenants/company-details/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        company_name: companyData.companyName,
        industry: companyData.industry,
        company_size: companyData.size,
        website: companyData.website,
        country: companyData.country,
        gst: companyData.gst,
        primary_focus: companyData.primaryFocus,
        hear_about_us: companyData.hearAboutUs,
        lead_id: leadId
      }),
    });
    
    return await response.json();
  } catch (error) {
    console.error('Error submitting company details:', error);
    return { success: false, error: error.message };
  }
};

export const finalizeOnboarding = async (leadId) => {
  try {
    const response = await fetch(`${API_URL}/tenants/onboarding/finalize/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ leadId }),
    });
    
    const data = await response.json();
    
    if (response.status === 200) {
      // Return the complete response for processing in the component
      return {
        success: true,
        data
      };
    } else {
      return {
        success: false,
        error: data.msg || 'Failed to finalize onboarding'
      };
    }
  } catch (error) {
    console.error('Error finalizing onboarding:', error);
    return {
      success: false,
      error: 'Network error occurred while finalizing account setup'
    };
  }
};